import { CustomFieldDto, CustomFieldValues } from "@/store/custom-fields/types";
import { NotifyNotificationRules } from "@/store/site-monitor/types";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import EmailNotificationRequest from "@/types/sv-data/events/EmailNotificationRequest";
import { EventNotification } from "@/types/sv-data/notification-rule/EventNotification";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import NotificationTemplateFormatOption from "@/types/sv-data/notification-templates/NotificationTemplateFormatOption";
import { NotificationTemplateManualField } from "@/types/sv-data/notification-templates/NotificationTemplateManualField";
import { hasEmptyRequiredCustomFields, hasInvalidCustomFieldValues } from "@/utils/CustomFieldLogic";

export const NotificationNoteMaxLength = 5000;
export const NoNotificationTemplateErrorIdentifier = -1;

export const NotificationValidationMessages = {
    NOTE_TOO_LONG: `Note must not exceed ${NotificationNoteMaxLength} characters.`,
    NO_CONTACTS_OR_GROUPS: "You must specify at least one contact or contact group.",
    EVENT_OUTCOME_INVALID: "Event outcome is not valid.",
    REQUIRED_CUSTOM_FIELDS_EMPTY: "Required custom fields are empty.",
    INVALID_CUSTOM_FIELDS: "Custom fields have invalid values.",
    TEMPLATE_FORMAT_NOT_SELECTED: "Template format is not selected.",
    NOTIFICATION_TEMPLATE_MISSING: "Notification template is missing."
};

export interface ContactOption {
	email: string;
	fullName: string | null;
	userID?: number;
}

export interface NotificationState extends Notification {
    skipped?: boolean;
}

export interface ManualFieldInput {
	field: NotificationTemplateManualField;
	value: string;
    valuePrePopulatedFromEmail?: boolean;
}

export interface Notification {
	notificationTemplate: NotificationTemplate;
    notificationTemplateFormat: NotificationTemplateFormatOption;

    contacts: ContactOption[];
	contactGroups: ContactGroup[];

	note: string;
	attachSitRep: boolean;
	templateNote: string;

    notificationManualFields: ManualFieldInput[];
	notificationCustomFields: ManualFieldInput[];
}

export interface NotificationState extends Notification {
    skipped?: boolean;
    isPreviouslySent?: boolean;
    hasContactsDifferentToRule?: boolean;
}

export const NotificationMixins = {
    methods: {
        /* Combine two arrays and remove duplicates */
        combineAndRemoveDuplicates<T>(array1: T[], array2: T[]): T[] {
            return combineAndRemoveDupes(array1, array2);
        },
        getCustomFieldValuesFromNotification(notification: Notification): CustomFieldValues[] {
            if (!notification || !notification.notificationCustomFields) return [];
            return notification.notificationCustomFields.map(cf => ({
                id: cf.field.id,
                value: cf.value
            }));
        },
        validateNotification(
            notification: Notification,
            eventOutcome: EventOutcomeAndNote,
            hasCustomFieldPermissions: boolean,
            notifyNotificationRules: NotifyNotificationRules,
            orderedCustomFieldList: CustomFieldDto[],
            notificationValidationErrorsByTemplate: Map<number, string[]> = new Map<number, string[]>()): void {
            const errors: string[] = [];

            // Note length check
            if (notification.note && notification.note.length > NotificationNoteMaxLength) {
                errors.push(NotificationValidationMessages.NOTE_TOO_LONG);
            }

            // Contacts/Contact Groups check
            const hasContacts = notification.contacts && notification.contacts.length > 0;
            const hasGroups = notification.contactGroups && notification.contactGroups.length > 0;

            if (!hasContacts && !hasGroups) {
                errors.push(NotificationValidationMessages.NO_CONTACTS_OR_GROUPS);
            }

            // Template checks
            if (notification.notificationTemplate != null) {
                if (!eventOutcome.valid) {
                    errors.push(NotificationValidationMessages.EVENT_OUTCOME_INVALID);
                }
                if (hasCustomFieldPermissions) {
                    let fieldsToCheck = notification.notificationCustomFields.map(cf => findCustomField(cf.field, orderedCustomFieldList)).filter(cf => !!cf);
                    const customFieldValues = this.getCustomFieldValuesFromNotification(notification);
                    if (hasEmptyRequiredCustomFields(customFieldValues, fieldsToCheck)) {
                        errors.push(NotificationValidationMessages.REQUIRED_CUSTOM_FIELDS_EMPTY);
                    }
                    if (hasInvalidCustomFieldValues(customFieldValues, fieldsToCheck)) {
                        errors.push(NotificationValidationMessages.INVALID_CUSTOM_FIELDS);
                    }
                }
                if (notification.notificationTemplateFormat === null) {
                    errors.push(NotificationValidationMessages.TEMPLATE_FORMAT_NOT_SELECTED);
                }
            }

            // Notification rules check
            if (!!notifyNotificationRules && !notification.notificationTemplate) {
                errors.push(NotificationValidationMessages.NOTIFICATION_TEMPLATE_MISSING);
            }

            if (!notification.notificationTemplate) {
                notificationValidationErrorsByTemplate.set(NoNotificationTemplateErrorIdentifier, errors);
                return;
            }

            notificationValidationErrorsByTemplate.set(
                notification.notificationTemplate?.notificationTemplateId,
                errors
            );
        },
        updateNotificationValidationErrors(notificationsToSend: Notification[],
            eventOutcome: EventOutcomeAndNote,
            hasCustomFieldPermissions: boolean,
            notifyNotificationRules: NotifyNotificationRules,
            notificationValidationErrorsByTemplate: Map<number, string[]>): void {
                notificationValidationErrorsByTemplate.clear();
                notificationsToSend.forEach(notification => {
                    this.validateNotification(notification,
                        eventOutcome,
                        hasCustomFieldPermissions,
                        notifyNotificationRules,
                        notificationValidationErrorsByTemplate);
                });
        },
        createNotification(
            userIds: number[] = [],
            contactGroupIds: string[] = [],
            emailAddresses: string[] = [],
            allContacts: ContactOption[] = [],
            contactGroups: ContactGroup[] = [],
            allNotificationTemplates: NotificationTemplate[] = [],
            notificationTemplateId: number = null,
            attachSitRep: boolean = false
        ): NotificationState {
            const notification: Notification = {
                contacts: [],
                contactGroups: [],
                note: "",
                attachSitRep: attachSitRep,
                notificationTemplate: null,
                notificationManualFields: [],
                notificationCustomFields: [],
                notificationTemplateFormat: null,
                templateNote: ""
            };

            if (userIds.length && allContacts.length) {
                notification.contacts = userIds.map(id => allContacts.firstOrDefault(c => c.userID === id));
            }

            // Create new ContactOption entries for any email addresses provided that do not correspond to existing contacts
            emailAddresses.forEach(email => {
                if (!notification.contacts.some(c => c.email === email)) {
                    notification.contacts.push({
                        email: email,
                        fullName: null
                    });
                }
            });

            if (contactGroupIds.length && contactGroups.length) {
                notification.contactGroups = contactGroupIds.map(id => contactGroups.firstOrDefault(g => g.contactGroupId === id));
            }
            if (notificationTemplateId && allNotificationTemplates.length) {
                notification.notificationTemplate = allNotificationTemplates.firstOrDefault(t => t.notificationTemplateId === notificationTemplateId);
            }

            const notificationState = notification as NotificationState;
            notificationState.skipped = false;

            return notificationState;
        },
        /*
        Group contacts and contact groups by their notification template ID
            Determines the Contact IDs and Contact Group IDs belonging to rules without a notification template
            Determines the Contact IDs and Contact Group IDs that belong to (potentially duplicate) notification templates from rules
            Returns ID values reflecting these values
        */
        groupContactsAndGroupsByTemplate(rules: NotificationRule[]): {
            contactsByTemplate: Map<number, number[]>,
            contactGroupsByTemplate: Map<number, string[]>,
            contactsWithNoTemplate: number[],
            contactGroupsWithNoTemplate: string[],
            emailAddressesWithNoTemplate: string[],
        } {
            const contactsByTemplate = new Map<number, number[]>();
            const contactGroupsByTemplate = new Map<number, string[]>();
            const contactsWithNoTemplate: number[] = [];
            const contactGroupsWithNoTemplate: string[] = [];
            const emailAddressesWithNoTemplate: string[] = [];

            if (!rules || rules.length === 0) {
                return {
                    contactsByTemplate,
                    contactGroupsByTemplate,
                    contactsWithNoTemplate: [],
                    contactGroupsWithNoTemplate: [],
                    emailAddressesWithNoTemplate: []
                };
            }
            rules.forEach(rule => {
                // If rule is associated to a Notification Template
                if (rule.notificationTemplateId) {
                    const tid = rule.notificationTemplateId;
                    // Add the rule's Contact IDs to the map entry for this template ID
                    contactsByTemplate.set(
                        tid,
                        combineAndRemoveDupes(contactsByTemplate.get(tid) || [], rule.userIds)
                    );
                    // Add the rule's Contact Group IDs to the map entry for this template ID
                    contactGroupsByTemplate.set(
                        tid,
                        combineAndRemoveDupes(contactGroupsByTemplate.get(tid) || [], rule.contactGroupIds)
                    );
                } else {
                    // If the rule doesn't have a template, add its Contact IDs and Contact Group IDs to the 'no template' arrays
                    contactsWithNoTemplate.push(...rule.userIds);
                    contactGroupsWithNoTemplate.push(...rule.contactGroupIds);
                    emailAddressesWithNoTemplate.push(...rule.emailAddresses);
                }
            });
            return {
                contactsByTemplate,
                contactGroupsByTemplate,
                contactsWithNoTemplate: combineAndRemoveDupes([], contactsWithNoTemplate),
                contactGroupsWithNoTemplate: combineAndRemoveDupes([], contactGroupsWithNoTemplate),
                emailAddressesWithNoTemplate: combineAndRemoveDupes([], emailAddressesWithNoTemplate)
            };
        },
        /*
            Groups all Contact Groups, Contact IDs, and Email Addresses
            Used when Predefined Templates feature is disabled
            Returns ID values reflecting these values
        */
        groupContactsAndGroups(rules: NotificationRule[]): {
            contactsByTemplate: Map<number, number[]>,
            contactGroupsByTemplate: Map<number, string[]>,
            contactsWithNoTemplate: number[],
            contactGroupsWithNoTemplate: string[],
            emailAddressesWithNoTemplate: string[],
        } {
            const contactsByTemplate = new Map<number, number[]>();
            const contactGroupsByTemplate = new Map<number, string[]>();
            const contactsWithNoTemplate: number[] = [];
            const contactGroupsWithNoTemplate: string[] = [];
            const emailAddressesWithNoTemplate: string[] = [];

            if (!rules || rules.length === 0) {
                return {
                    contactsByTemplate,
                    contactGroupsByTemplate,
                    contactsWithNoTemplate: [],
                    contactGroupsWithNoTemplate: [],
                    emailAddressesWithNoTemplate: []
                };
            }

            // Since Predefined Templates are disabled, all contacts and groups belong to 'no template'
            rules.forEach(rule => {
                contactsWithNoTemplate.push(...rule.userIds);
                contactGroupsWithNoTemplate.push(...rule.contactGroupIds);
                emailAddressesWithNoTemplate.push(...rule.emailAddresses);
            });

            // We return empty maps for contactsByTemplate and contactGroupsByTemplate
            // This is to allow for simplified handling in the calling code

            return {
                contactsByTemplate,
                contactGroupsByTemplate,
                contactsWithNoTemplate: combineAndRemoveDupes([], contactsWithNoTemplate),
                contactGroupsWithNoTemplate: combineAndRemoveDupes([], contactGroupsWithNoTemplate),
                emailAddressesWithNoTemplate: combineAndRemoveDupes([], emailAddressesWithNoTemplate)
            };
        },
        createManualAndCustomFieldsForTemplate(notification: NotificationState, previouslySentTemplate: EventNotification): void {
            const availableManualFields: ManualFieldInput[] = [];
            const availableCustomFields: ManualFieldInput[] = [];

            let parsedData = null;
            if (previouslySentTemplate != null)
            {
                parsedData = JSON.parse(previouslySentTemplate.notificationData);

                // If a notification has already been skipped, mark as not previously sent
                // Otherwise, mark as previously sent
                notification.isPreviouslySent = notification.skipped ? false : true;
            }

            // Iterate through the manual fields for the associated template and append a key-value pair for each
            const manualFields = notification.notificationTemplate?.manualFields;
            if (manualFields != null) {
                manualFields.forEach(element => {
                    let value = "";
                    if (parsedData != null
                        && parsedData.createNotificationDocumentRequest?.manualInputFields != null
                        && parsedData.createNotificationDocumentRequest?.manualInputFields[element.id] != null) {
                        value = parsedData.createNotificationDocumentRequest.manualInputFields[element.id];
                    }
                    availableManualFields.push({
                        field: element,
                        value: value,
                        valuePrePopulatedFromEmail: value ? true : false
                    });
                });
            }

            // Iterate through the custom fields for the associated template and append a key-value pair for each
            const customFields = notification.notificationTemplate?.customFields;
            if (customFields != null) {
                customFields.forEach(element => {
                    let value = "";

                    if (parsedData != null
                        && parsedData?.createNotificationDocumentRequest?.customFieldInputs != null
                        && parsedData?.createNotificationDocumentRequest?.customFieldInputs[element.id] != null) {
                        value = parsedData?.createNotificationDocumentRequest.customFieldInputs[element.id];
                    }

                    const doesValueExist = availableCustomFields.some(f => f.field.id === element.id && f.value && f.value.length > 0);

                    if (doesValueExist) {
                        return;
                    }

                    availableCustomFields.push({
                        field: element,
                        value: value
                    });
                });
            }

            // Set the relevant fields on the notification passed into the mixin
            notification.notificationManualFields = availableManualFields;
            notification.notificationCustomFields = availableCustomFields;
        },
        notificationToEmailRequest(notification: Notification): EmailNotificationRequest {
            return {
                contacts: notification.contacts.map(c => ({
                    fullName: c.fullName,
                    email: c.email
                })),
                contactGroupIds: notification.contactGroups.map(cg => cg.contactGroupId),
                contactGroups: notification.contactGroups,
                note: notification.note,
                attachSitRep: notification.attachSitRep,
                createNotificationDocumentRequest: notification.notificationTemplate
                    ? {
                        notificationTemplateId: notification.notificationTemplate.notificationTemplateId,
                        notificationDocumentTypeId: notification.notificationTemplateFormat?.id ?? null,
                        note: notification.note,
                        manualInputFields: notification.notificationManualFields.reduce((map, obj) => {
                            map[obj.field.id] = obj.value;
                            return map;
                        }, {}),
                        customFieldInputs: notification.notificationCustomFields.reduce((map, obj) => {
                            map[obj.field.id] = obj.value;
                            return map;
                        }, {})
                    }
                    : null
            };
        },
        bindContactsFromPreviousNotification(notification: NotificationState, previouslySentTemplate: EventNotification): void {
            if (!previouslySentTemplate) {
                return;
            }

            const parsedData = JSON.parse(previouslySentTemplate.notificationData) as Notification;
            if (!parsedData) {
                return;
            }

            // If the notification object has contacts or contact groups, it was created by a rule
            const notificationCreatedByRule = notification.contacts?.length !== 0 || notification.contactGroups?.length !== 0;

            if (parsedData.contacts && Array.isArray(parsedData.contacts)) {
                const newContacts = parsedData.contacts.filter((contact =>
                    notification.contacts?.length === 0 ||
                    ( contact.email != null && notification.contacts.some(notiContact => notiContact.email !== contact.email) ||
                    (contact.userID != null && notification.contacts.some(notiContact => notiContact.userID !== contact.userID)
                )))).map(contact => ({
                    email: contact.email,
                    fullName: contact.fullName,
                    userID: contact.userID
                }));

                if (newContacts.length > 0) {
                    notification.hasContactsDifferentToRule = notificationCreatedByRule;
                    notification.contacts = notification.contacts.concat(newContacts);
                }
            }

            if (parsedData.contactGroups && Array.isArray(parsedData.contactGroups)) {
                const currentGroupIds = notification.contactGroups.map(cg => cg.contactGroupId);
                const newContacts = parsedData.contactGroups.filter((contactGroup =>
                    currentGroupIds?.length === 0 ||
                    !currentGroupIds.includes(contactGroup.contactGroupId)
                )).map(contactGroup => ({
                    contactGroupId: contactGroup.contactGroupId,
                    title: contactGroup.title,
                    groupId: contactGroup.groupId,
                    suiteTenantId: contactGroup.suiteTenantId
                }));
                if (newContacts.length > 0) {
                    notification.hasContactsDifferentToRule = notificationCreatedByRule;
                    notification.contactGroups = notification.contactGroups.concat(newContacts);
                }
            }

            notification.contacts = combineAndRemoveDupesByKeys(notification.contacts, [], ["email", "userID"]);
            notification.contactGroups = combineAndRemoveDupesByKeys(notification.contactGroups, [], ["contactGroupId"]);
        },
        bindPreviouslySentNotificationFields(notification: NotificationState, previouslySentTemplate: EventNotification): void {
            if (!notification || !previouslySentTemplate) {
                return;
            }

            let parseData = null;

            try {
                parseData = JSON.parse(previouslySentTemplate.notificationData) as NotificationState;
            } catch (error) {
                console.error("Error parsing previously sent notification data:", error);
                return;
            }

            if (!parseData) {
                return;
            }

            notification.note = parseData?.note || "";
        }
    }
}

function findCustomField(customField: NotificationTemplateManualField, customFieldList: CustomFieldDto[]): CustomFieldDto {
    return customFieldList.find(cf => cf.id == customField.id);
}

function combineAndRemoveDupes<T>(array1: T[], array2: T[]): T[] {
    return Array.from(new Set((array1 || []).concat(array2 || [])));
}

// combineAndRemoveDupes for objects based on one or more key values
export function combineAndRemoveDupesByKeys<T>(array1: T[], array2: T[], keys: (keyof T)[]): T[] {
    // Example usage:
    // const result = combineAndRemoveDupesByKeys(array1, array2, ['id', 'name']);

    const combinedArray = (array1 || []).concat(array2 || []);
    const uniqueMap = new Map<string, T>();
    combinedArray.forEach(item => {
        const key = keys.map(k => item[k]).join("|");
        if (!uniqueMap.has(key)) {
            uniqueMap.set(key, item);
        }
    });
    return Array.from(uniqueMap.values());
}